import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Modal } from "./Guests";
import { X, Inbox, Copy, UserPlus, MailCheck, MailX } from "lucide-react";

const STATUS_BADGE = { pending: "pgm-badge-orange", invited: "pgm-badge-olive", declined: "pgm-badge" };

export default function AccessRequests() {
  const [list, setList] = useState([]);
  const [inviting, setInviting] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/access-requests");
    setList(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (r) => {
    setError("");
    setInviting(r.id);
    try {
      const { data } = await api.post(`/admin/access-requests/${r.id}/invite`);
      setInviteResult({ ...data, name: r.name, show_name: r.show_name });
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Invite failed");
    } finally {
      setInviting(null);
    }
  };

  const setStatus = async (id, status) => { await api.put(`/admin/access-requests/${id}`, { status }); load(); };
  const remove = async (id) => { if (window.confirm("Delete this request?")) { await api.delete(`/admin/access-requests/${id}`); load(); } };

  const pending = list.filter(r => r.status === "pending").length;

  return (
    <div className="pgm-reveal">
      <div className="pgm-micro">◆ Super Admin</div>
      <h1 className="pgm-display" style={{ fontSize: 44 }}>Access Requests</h1>
      <p style={{ color: "var(--pgm-wine)", opacity: 0.75, marginBottom: 22 }}>
        Inbound requests from the public landing page. Invite creates an admin account + show with one click and emails a temporary password.
      </p>

      {error && <div className="pgm-callout" style={{ borderLeftColor: "var(--pgm-orange)", marginBottom: 16 }} data-testid="invite-error"><strong>Couldn't invite:</strong> {error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 22 }}>
        <div className="pgm-stat"><div className="pgm-micro">Pending</div><div className="pgm-stat-value">{pending}</div></div>
        <div className="pgm-stat"><div className="pgm-micro">Total</div><div className="pgm-stat-value">{list.length}</div></div>
      </div>

      {list.length === 0 ? (
        <div className="pgm-callout"><Inbox size={16} style={{ verticalAlign: "middle", marginRight: 8 }} /><strong>No access requests yet.</strong> Share your landing page URL to start collecting requests.</div>
      ) : (
        <table className="pgm-table" data-testid="requests-table">
          <thead><tr><th>Name</th><th>Email</th><th>Show</th><th>URL</th><th>Message</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {list.map(r => (
              <tr key={r.id} data-testid={`request-row-${r.id}`}>
                <td><strong>{r.name}</strong></td>
                <td>{r.email}</td>
                <td>{r.show_name}</td>
                <td>{r.show_url ? <a className="pgm-link" href={r.show_url} target="_blank" rel="noreferrer">link</a> : "—"}</td>
                <td style={{ maxWidth: 280, fontSize: 12 }}>{r.message || "—"}</td>
                <td><span className={`pgm-badge ${STATUS_BADGE[r.status] || "pgm-badge"}`}>{r.status}</span></td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {r.status !== "invited" && (
                    <button className="pgm-btn pgm-btn-olive" style={{ padding: "5px 10px", fontSize: 10, marginRight: 4 }} onClick={() => invite(r)} disabled={inviting === r.id} data-testid={`req-invite-${r.id}`}>
                      <UserPlus size={11} /> {inviting === r.id ? "Inviting…" : "Invite"}
                    </button>
                  )}
                  {r.status !== "declined" && r.status !== "invited" && <button className="pgm-btn pgm-btn-ghost" style={{ padding: "5px 10px", fontSize: 10, marginRight: 4 }} onClick={() => setStatus(r.id, "declined")} data-testid={`req-decline-${r.id}`}>Decline</button>}
                  <button className="pgm-btn pgm-btn-ghost" style={{ padding: "5px 10px", fontSize: 10, borderColor: "var(--pgm-orange)", color: "var(--pgm-orange)" }} onClick={() => remove(r.id)} data-testid={`req-delete-${r.id}`}><X size={11} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {inviteResult && (
        <Modal onClose={() => setInviteResult(null)} title="Admin account created" width={540}>
          <div data-testid="invite-result-modal">
            <div className="pgm-callout" style={{ background: "var(--pgm-olive)", color: "var(--pgm-cream)", marginBottom: 14 }}>
              <strong>{inviteResult.name}</strong> now has an admin account for <strong>{inviteResult.show_name}</strong>. They'll be asked to set a new password on first login.
            </div>
            <div className="pgm-card" style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}><strong>Email:</strong> {inviteResult.email}</div>
              <div><strong>Temporary password:</strong> <code data-testid="invite-temp-password">{inviteResult.temp_password}</code></div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 14 }} data-testid="invite-email-status">
              {inviteResult.email_sent ? (
                <><MailCheck size={15} color="var(--pgm-olive)" /> Invite email sent to {inviteResult.email}</>
              ) : (
                <><MailX size={15} color="var(--pgm-orange)" /> Email not delivered ({inviteResult.email_reason || "unknown"}) — share the credentials manually.</>
              )}
            </div>
            <button className="pgm-btn" onClick={() => navigator.clipboard.writeText(`Email: ${inviteResult.email}\nPassword: ${inviteResult.temp_password}`)} data-testid="invite-copy-btn">
              <Copy size={14} /> Copy credentials
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
